// Acetazolamide for altitude sickness prevention is entirely off-label.
// Single brand, 125 mg twice daily (half a 250 mg tablet). Maximum 15
// tablets per supply. Sulfonamide cross-sensitivity exclusion list is
// the key clinical risk — every item transcribed exactly. The aspirin
// interaction is a hard exclusion (not a caution).

import { registerTier2Config } from "../tier2ConfigRegistry";
import { PGD_IDS } from "../../pgds/pgdIds";

registerTier2Config({
  pgdId: PGD_IDS.ACETAZOLAMIDE,
  displayName: "Acetazolamide (altitude sickness prevention)",
  serviceType: "supply",
  minimumAgeYears: 18,
  indicationSummary:
    "Prevention of acute mountain sickness (altitude sickness) for travel above 2,500 m (8,202 ft). Off-label use \u2014 acetazolamide is not licensed for this indication but its use is supported by national travel health guidance.",

  brands: [
    {
      brandId: "acetazolamide-250mg",
      brandName: "Acetazolamide 250 mg tablets",
      minimumAgeYears: 18,
      route: "supply",
      doseDescription: "125 mg (half a 250 mg tablet) twice daily",
      administrationNotes:
        "Supply 250 mg tablets with a pill cutter. Label: half a tablet (125 mg) twice daily. Advise the patient to use the pill cutter provided and to discard any uneven halves. Start 1 day before ascent. Continue for at least 2 days once acclimatised or once descent has begun, then stop \u2014 unless early symptoms of altitude sickness reappear.",
    },
  ],

  schedules: [
    {
      scheduleId: "acetazolamide-twice-daily",
      label: "Twice daily \u2014 off-label for altitude sickness prevention \u2014 start 1 day before ascent",
      offLabel: true,
      offLabelRationale:
        "Acetazolamide is not licensed for the prevention of altitude sickness. This off-label use is supported by national travel health guidance. Supply requires documented rationale and patient consent to off-label use.",
      doses: [
        {
          doseNumber: 1,
          label: "Start: 1 day before ascent \u2014 first dose",
        },
      ],
      completionNotes:
        "Take twice daily (morning and evening). Start 1 day before ascent. Continue for at least 2 days once acclimatised or once beginning descent, then stop unless symptoms return. Maximum 15 tablets per supply. Acetazolamide does not permit continued ascent if symptoms of altitude sickness appear \u2014 the patient must remain at the same altitude until symptoms resolve; if not resolved after 24 hours, descend at least 500 m.",
    },
  ],

  capturesPriorDoseHistory: false,

  supplyGates: [
    "Patient is travelling above 2,500 m (8,202 ft) and is at risk of acute mountain sickness.",
    "Patient age confirmed as 18 years or over.",
    "Patient has been informed that this supply is for an off-label use (altitude sickness prevention) and has given informed consent. This must be documented.",
  ],

  exclusions: [
    {
      checkId: "acetaz-ex-age",
      question: "Is the patient under 18 years of age?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Acetazolamide for altitude sickness prevention under this PGD is restricted to patients aged 18 and over. Do not supply.",
      referralMessage: "Refer to the patient's GP for advice.",
    },
    {
      checkId: "acetaz-ex-sulfonamide",
      question:
        "Does the patient have known hypersensitivity to sulfonamides or sulfonamide derivatives? This includes: co-trimoxazole, sulfamethoxazole, sulfasalazine, sulfonylurea antidiabetic medicines (e.g. gliclazide, glibenclamide), sulfonamide-group diuretics (including thiazide diuretics such as bendroflumethiazide and hydrochlorothiazide, and loop diuretics such as furosemide and bumetanide), and other sulfonamide antibiotics.",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Acetazolamide is a sulfonamide derivative and is contraindicated in patients with hypersensitivity to sulfonamides or sulfonamide derivatives. Do not supply.",
      referralMessage: "Refer to the patient's GP for advice on altitude sickness prevention.",
    },
    {
      checkId: "acetaz-ex-renal-hepatic",
      question: "Does the patient have renal impairment or hepatic impairment of any degree?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Renal or hepatic impairment of any degree is a contraindication to acetazolamide under this PGD. Do not supply.",
      referralMessage: "Refer to the patient's GP for individual assessment.",
    },
    {
      checkId: "acetaz-ex-glaucoma-epilepsy",
      question: "Does the patient have chronic non-congestive angle-closure glaucoma, epilepsy, or is the patient taking anticonvulsant medicines (including phenytoin, primidone, or carbamazepine)?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Chronic non-congestive angle-closure glaucoma, epilepsy, and concurrent anticonvulsant use are each a contraindication to acetazolamide under this PGD. Do not supply.",
      referralMessage: "Refer to the patient's GP or neurologist for advice on altitude sickness prevention.",
    },
    {
      checkId: "acetaz-ex-electrolyte",
      question: "Does the patient have low sodium, low potassium, adrenocortical insufficiency, hyperchloraemic acidosis, or marked kidney or liver dysfunction?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "These conditions are a contraindication to acetazolamide. Do not supply.",
      referralMessage: "Refer to the patient's GP.",
    },
    {
      checkId: "acetaz-ex-psychiatric",
      question: "Does the patient have severe depression, generalised anxiety disorder, psychosis, schizophrenia, suicidal thoughts or behaviour, or any other significant psychiatric disorder?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Significant psychiatric disorders are a contraindication to acetazolamide under this PGD. Do not supply.",
      referralMessage: "Refer to the patient's GP.",
    },
    {
      checkId: "acetaz-ex-pulmonary-record",
      question: "Does the patient have pulmonary obstruction, emphysema, or any condition causing significant impairment of alveolar ventilation?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Conditions causing impairment of alveolar ventilation are a contraindication to acetazolamide. Do not supply.",
      referralMessage: "Refer to the patient's GP for advice on altitude travel.",
    },
    {
      checkId: "acetaz-ex-pregnancy-breastfeeding-calculi-oedema",
      question: "Is the patient pregnant or breastfeeding? Or does the patient have a history of renal calculi (kidney stones) or previous non-cardiogenic pulmonary oedema following acetazolamide use?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Pregnancy, breastfeeding, a history of renal calculi, and a previous history of non-cardiogenic pulmonary oedema on acetazolamide are each a contraindication. Do not supply.",
      referralMessage: "Refer to the patient's GP.",
    },
  ],

  cautions: [
    {
      checkId: "acetaz-caut-lithium",
      question: "Is the patient taking lithium?",
      cautionMessage:
        "Acetazolamide increases lithium excretion and may reduce lithium plasma levels, potentially reducing efficacy. Monitor lithium levels and advise the patient to inform their GP.",
    },
    {
      checkId: "acetaz-caut-anticoagulants",
      question: "Is the patient taking oral anticoagulants or hypoglycaemic medicines?",
      cautionMessage:
        "Acetazolamide may potentiate the effect of oral anticoagulants and hypoglycaemic agents. Advise the patient to monitor more closely and notify their GP of the supply.",
    },
    {
      checkId: "acetaz-caut-ciclosporin",
      question: "Is the patient taking ciclosporin?",
      cautionMessage: "Acetazolamide may raise ciclosporin plasma levels. Refer to the patient's GP before supply.",
    },
    {
      checkId: "acetaz-caut-altitude-safety",
      question: "Has the pharmacist counselled the patient on altitude sickness warning signs and the importance of descent?",
      cautionMessage:
        "Acetazolamide does not permit continued ascent if symptoms of altitude sickness develop. Advise the patient: if symptoms appear, remain at the same altitude until fully resolved; if not resolved after 24 hours, descend at least 500 m immediately. High-altitude cerebral oedema (HACE: severe headache, confusion, loss of coordination, vomiting) and high-altitude pulmonary oedema (HAPE: breathlessness at rest, cough, cyanosis) are life-threatening emergencies requiring immediate descent and urgent medical care. Document that this advice was given.",
    },
    {
      checkId: "acetaz-caut-aspirin",
      question: "Is the patient taking high-dose aspirin or salicylates?",
      cautionMessage:
        "Concurrent use of acetazolamide and high-dose aspirin or salicylates should be avoided due to the risk of severe metabolic acidosis and increased CNS toxicity of aspirin. Advise the patient not to take aspirin or aspirin-containing products while using acetazolamide. Document this advice.",
    },
  ],

  requiresICVP: false,
  requiresLariamCard: false,
  requiresYellowCardDiscussion: false,
  requiresCHISNotificationUnder19: false,
  requiresGPNotification: true,
  standardObservationMinutes: 0,
  extendedObservationMinutes: 0,

  patientAdvicePoints: [
    "Take half a tablet (125 mg) twice daily (morning and evening). Use the pill cutter provided and discard any uneven halves.",
    "This medicine is being used outside its product licence (off-label) for altitude sickness prevention. This use is supported by national travel health guidance. You have given your consent to this off-label use.",
    "Start taking acetazolamide 1 day before you begin your ascent.",
    "Continue for at least 2 days once you are acclimatised or once you begin to descend, then stop \u2014 unless symptoms of altitude sickness return, in which case continue and seek advice.",
    "Do NOT take aspirin or aspirin-containing products while taking acetazolamide.",
    "Acetazolamide does not mean you can ascend faster than normal. Ascend gradually and allow time to acclimatise.",
    "If symptoms of altitude sickness develop (headache, nausea, fatigue, dizziness), stop your ascent and rest at the same altitude until symptoms fully resolve. If symptoms do not resolve within 24 hours, descend at least 500 m immediately.",
    "High-altitude cerebral oedema (HACE) \u2014 symptoms: severe headache that does not respond to painkillers, confusion, loss of coordination, difficulty walking, vomiting. This is a life-threatening emergency \u2014 descend immediately and seek urgent medical help.",
    "High-altitude pulmonary oedema (HAPE) \u2014 symptoms: breathlessness at rest, persistent cough, cyanosis (blue lips or fingertips), pink or frothy sputum. This is a life-threatening emergency \u2014 descend immediately and seek urgent medical help.",
  ],
});
